import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { DEFAULT_STRUCTURE_TAB, StructureTabKey, isStructureTabKey } from './structureTabs';

export interface UseStructureActiveTabArgs {
  visibleTabs: StructureTabKey[];
  /**
   * Whether `visibleTabs` can be trusted yet — i.e. *both* the structure and the
   * user's permissions have arrived. Until they have, the tab set is derived
   * from an empty property-type list and an empty permission set, so clamping
   * would discard a valid deep link before the tab it names exists.
   *
   * Both halves matter. The query cache is persisted to `localStorage` and
   * restored synchronously at boot, so on a reload the structure is available
   * immediately while the permissions request is still in flight — which made
   * `?tab=related` clamp itself back to `?tab=details` on every refresh.
   */
  isTabSetKnown: boolean;
}

export interface StructureActiveTab {
  activeTab: StructureTabKey;
  setActiveTab: (tab: StructureTabKey) => void;
}

/**
 * Tracks the active tab in the URL rather than component state, so a tab is
 * shareable and survives a reload — the same contract as the Part Type page.
 */
export const useStructureActiveTab = ({
  visibleTabs,
  isTabSetKnown,
}: UseStructureActiveTabArgs): StructureActiveTab => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  const requestedTab = searchParams.get('tab');

  const [activeTab, setActiveTabState] = useState<StructureTabKey>(() =>
    isStructureTabKey(requestedTab) ? requestedTab : DEFAULT_STRUCTURE_TAB
  );

  // Follow the URL when something other than the tab strip moves it — Back and
  // Forward, or a link into a specific tab from elsewhere in the app. Without
  // this the address bar and the rendered tab drift apart.
  useEffect(() => {
    if (isStructureTabKey(requestedTab)) {
      if (requestedTab !== activeTab) {
        setActiveTabState(requestedTab);
      }
      return;
    }
    // The URL no longer names a tab we render — Back to a link without `?tab=`,
    // or a stale `?tab=location`. Fall back rather than leaving the strip on a
    // tab the address bar does not select.
    if (activeTab !== DEFAULT_STRUCTURE_TAB) {
      setActiveTabState(DEFAULT_STRUCTURE_TAB);
    }
    // Deliberately keyed on the URL alone: including activeTab would fight the
    // clamp below, which corrects the tab and then writes the URL to match.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedTab]);

  const setActiveTab = useCallback(
    (tab: StructureTabKey) => {
      setActiveTabState(tab);

      const next = new URLSearchParams(location.search);
      next.set('tab', tab);
      // `replace` keeps tab switching out of the browser history, so Back still
      // returns to wherever the user came from rather than walking the tabs.
      navigate({ search: `?${next.toString()}`, hash: location.hash }, { replace: true });
    },
    [navigate, location.search, location.hash]
  );

  useEffect(() => {
    if (!isTabSetKnown || visibleTabs.length === 0) {
      return;
    }
    if (!visibleTabs.includes(activeTab)) {
      // Goes through setActiveTab so the URL follows; leaving ?tab= pointing at
      // a tab that is not rendered would survive a reload and clamp again.
      setActiveTab(
        visibleTabs.includes(DEFAULT_STRUCTURE_TAB) ? DEFAULT_STRUCTURE_TAB : visibleTabs[0]
      );
    }
  }, [activeTab, isTabSetKnown, setActiveTab, visibleTabs]);

  return { activeTab, setActiveTab };
};
